import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Map,
  Pencil,
  Trash2,
  Loader2,
  AlertTriangle,
} from 'lucide-react'
import {
  getAdminHunts,
  deleteHunt,
  publishHunt,
  unpublishHunt,
} from '../api'
import type { AdminHunt } from '../api'

function StatusBadge({
  status,
  onClick,
}: {
  status: string
  onClick?: () => void
}) {
  const isPublished = status === 'published'
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 ${
        isPublished
          ? 'bg-green-100 text-green-700'
          : 'bg-stone-100 text-stone-600'
      }`}
    >
      {isPublished ? 'Published' : 'Draft'}
    </button>
  )
}

function CompletionBar({ rate }: { rate: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 overflow-hidden rounded-full bg-stone-200">
        <div
          className="h-full rounded-full bg-[#f59e0b] transition-all"
          style={{ width: `${Math.min(rate, 100)}%` }}
        />
      </div>
      <span className="text-sm text-stone-600">{rate.toFixed(0)}%</span>
    </div>
  )
}

function DeleteModal({
  huntTitle,
  onConfirm,
  onCancel,
}: {
  huntTitle: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-stone-900">Delete Hunt</h3>
        </div>
        <p className="mb-6 text-sm text-stone-600">
          Are you sure you want to delete{' '}
          <span className="font-medium text-stone-900">{huntTitle}</span>? This
          action cannot be undone and will remove all waypoints and progress
          data.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="rounded-lg border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

export default function HuntListPage() {
  const navigate = useNavigate()
  const [hunts, setHunts] = useState<AdminHunt[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingHunt, setDeletingHunt] = useState<AdminHunt | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const loadHunts = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getAdminHunts()
      setHunts(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hunts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadHunts()
  }, [loadHunts])

  const handleToggleStatus = async (hunt: AdminHunt) => {
    try {
      setActionLoading(hunt.id)
      if (hunt.status === 'published') {
        await unpublishHunt(hunt.id)
      } else {
        await publishHunt(hunt.id)
      }
      await loadHunts()
    } catch (err) {
      console.error('Failed to toggle status:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const handleDelete = async () => {
    if (!deletingHunt) return
    try {
      setActionLoading(deletingHunt.id)
      await deleteHunt(deletingHunt.id)
      setDeletingHunt(null)
      await loadHunts()
    } catch (err) {
      console.error('Failed to delete hunt:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const getCompletionRate = (hunt: AdminHunt) => {
    if (hunt.total_starts === 0) return 0
    return (hunt.total_completions / hunt.total_starts) * 100
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#f59e0b]" />
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Top bar */}
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-stone-900">My Hunts</h1>
        <button
          onClick={() => navigate('/admin/hunts/new')}
          className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#78350f] to-[#92400e] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          Create Hunt
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {hunts.length === 0 ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone-300 py-16">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-stone-100">
            <Map className="h-8 w-8 text-stone-400" />
          </div>
          <h3 className="mb-1 text-lg font-semibold text-stone-700">
            No hunts yet
          </h3>
          <p className="mb-6 text-sm text-stone-500">
            Create your first treasure hunt to get started.
          </p>
          <button
            onClick={() => navigate('/admin/hunts/new')}
            className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#78350f] to-[#92400e] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            Create Hunt
          </button>
        </div>
      ) : (
        /* Hunt table */
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                  Hunt Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                  Waypoints
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                  Total Starts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">
                  Completion Rate
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-stone-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {hunts.map((hunt) => (
                <tr key={hunt.id} className="transition-colors hover:bg-stone-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-stone-900">
                        {hunt.title}
                      </span>
                      {hunt.status === 'published' &&
                        (!hunt.estimated_duration_minutes ||
                          !hunt.total_distance_meters) && (
                          <span
                            title={
                              !hunt.estimated_duration_minutes &&
                              !hunt.total_distance_meters
                                ? 'Missing duration and distance — add waypoints and set estimated duration'
                                : !hunt.estimated_duration_minutes
                                  ? 'Missing estimated duration — set in hunt editor'
                                  : 'Missing distance — add waypoints to auto-calculate'
                            }
                          >
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          </span>
                        )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {actionLoading === hunt.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
                    ) : (
                      <StatusBadge
                        status={hunt.status}
                        onClick={() => handleToggleStatus(hunt)}
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">
                    {hunt.waypoint_count}
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">
                    {hunt.total_starts}
                  </td>
                  <td className="px-6 py-4">
                    <CompletionBar rate={getCompletionRate(hunt)} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/admin/hunts/${hunt.id}`)}
                        className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeletingHunt(hunt)}
                        className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-red-50 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirm modal */}
      {deletingHunt && (
        <DeleteModal
          huntTitle={deletingHunt.title}
          onConfirm={handleDelete}
          onCancel={() => setDeletingHunt(null)}
        />
      )}
    </div>
  )
}
